import React, { useState, useEffect } from 'react';
import { Loader2, User } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface NameCompletionModalProps {
  userId: string;
  onComplete: () => void;
}

export function NameCompletionModal({ userId, onComplete }: NameCompletionModalProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();

    if (!trimmedFirst || !trimmedLast) {
      setError('Please enter both your first and last name.');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          first_name: trimmedFirst,
          last_name: trimmedLast,
          full_name: `${trimmedFirst} ${trimmedLast}`
        })
        .eq('id', userId);

      if (profileError) throw profileError;

      await supabase.auth.updateUser({
        data: {
          first_name: trimmedFirst,
          last_name: trimmedLast,
          full_name: `${trimmedFirst} ${trimmedLast}`
        }
      });

      onComplete();
    } catch (err) {
      console.error('Error saving name:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-charcoal rounded-xl shadow-xl border border-sand/20 dark:border-charcoal w-full max-w-md mx-4 p-8">
        <div className="flex items-center justify-center w-12 h-12 bg-electric/10 text-electric rounded-full mx-auto mb-4">
          <User className="h-6 w-6" />
        </div>
        <h2 className="text-xl font-bold text-midnight dark:text-soft-white text-center mb-2">
          Complete Your Profile
        </h2>
        <p className="text-slate dark:text-slate/80 text-center text-sm mb-6">
          Please enter your name so we know who you are.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate dark:text-soft-white/80 mb-1">
              First Name
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => { setFirstName(e.target.value); setError(null); }}
              className="w-full px-3 py-2 border border-sand dark:border-charcoal/60 rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent bg-white dark:bg-midnight text-midnight dark:text-soft-white"
              placeholder="Enter your first name"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate dark:text-soft-white/80 mb-1">
              Last Name
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => { setLastName(e.target.value); setError(null); }}
              className="w-full px-3 py-2 border border-sand dark:border-charcoal/60 rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent bg-white dark:bg-midnight text-midnight dark:text-soft-white"
              placeholder="Enter your last name"
            />
          </div>

          {error && (
            <p className="text-sm text-crimson">{error}</p>
          )}

          <button
            type="submit"
            disabled={saving || !firstName.trim() || !lastName.trim()}
            className="w-full py-2.5 px-4 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50 font-medium transition-colors"
          >
            {saving ? (
              <Loader2 className="h-5 w-5 animate-spin mx-auto" />
            ) : (
              'Continue'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}